import { Booking } from '../../../../domain/Booking'
import { BookingService } from '../../../../services/booking.service'
import { Business } from '../../../../domain/Business'
import { Charge } from '@app/domain/Charge'
import {
    Component,
    EventEmitter, inject,
    Input,
    OnInit,
    Output,
} from '@angular/core'
import { ContextService } from '@services/context.service'
import { DurationUnit } from '../../pipes/duration.pipe'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Venue } from '@app/domain/Venue'
import { finalize, startWith, takeUntil } from 'rxjs'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'

@Component({
    selector: 'app-add-charge',
    templateUrl: './add-charge.component.html',
})
export class AddChargeComponent implements OnInit {

    @Input() business!: Business
    @Input() venue!: Venue
    @Input() booking!: Booking
    @Input() charge: Charge | null = null
    @Input() amountOnly = false
    @Input() forceNoExpiry = false
    @Output() bookingChanged = new EventEmitter<Booking>()
    canSendLinks = false
    isAddingCharge = false
    chargeType!: 'cancellationCharge' | 'depositCharge'
    form!: FormGroup
    previousExpiryDate!: Date | null
    expiryMinutes!: number | null
    expiryDate: Date | null = null

    fb = inject(FormBuilder)
    private onDestroy$ = new EventEmitter<void>()

    constructor(
        private contextService: ContextService,
        private bookingService: BookingService,
        private modal: NgbActiveModal
    ) { }

    ngOnInit() {
        this.canSendLinks = this.business.paymentsAccountForCapability(
            this.chargeType === 'cancellationCharge' ? 'CancellationChargePayments' : 'DepositPayments'
        ) !== null
        this.previousExpiryDate = this.booking.expiryDate()
        this.charge = this.charge ?? this.booking.pendingCancellationCharge ?? this.booking.pendingDepositCharge ?? null
        if (this.charge === null && !this.amountOnly) {
            const defaultCancellationCharge = this.venue.cancellationChargeForPartySize(
                this.booking.reasonId,
                this.booking.eventId,
                this.booking.size,
                this.booking.start
            )
            const defaultDepositCharge = this.venue.depositChargeForPartySize(
                this.booking.reasonId,
                this.booking.eventId,
                this.booking.size,
                this.booking.start
            )
            this.charge = this.chargeType === 'cancellationCharge' ? defaultCancellationCharge : defaultDepositCharge
        }
        if (this.charge === null) {
            const expiryMinutes = this.venue.diaryPreferences.pendingPaymentExpiryMinutes
            this.expiryMinutesChanged(expiryMinutes)
        } else {
            this.expiryMinutesChanged(-1)
        }
        let defaultDeliveryMethod = null
        if (this.booking.emailAddress !== null) {
            defaultDeliveryMethod = 'email'
        } else if (this.booking.phoneNumber !== null) {
            defaultDeliveryMethod = 'sms'
        }
        const defaultToPaidInPerson = this.booking.deposit?.paymentMethodId === null || !this.canSendLinks
        this.form = this.fb.group({
            linkDeliveryMethod: [defaultDeliveryMethod, Validators.required],
            paidInPerson: [defaultToPaidInPerson],
            isPaidImmediately: [false, Validators.required],
        })

        this.form.get('paidInPerson')?.valueChanges
            .pipe(
                startWith(this.form.get('paidInPerson')?.value),
                takeUntil(this.onDestroy$)
            )
            .subscribe(_ => {
                this.updateValidation()
            })
    }

    dismiss() {
        this.modal.dismiss()
    }

    save() {
        if (this.form.invalid) {
            this.form.markAllAsTouched()
            return
        }
        if (this.charge === null) {
            return
        }
        const isCreatingNewCharge = this.booking.pendingCancellationCharge === null
            && this.booking.pendingDepositCharge === null
        if (isCreatingNewCharge) {
            this.addCharge()
        } else {
            this.updateCharge()
        }
    }

    expiryMinutesChanged(expiryMinutes: number | null) {
        this.expiryMinutes = this.forceNoExpiry ? null : expiryMinutes
        if (this.expiryMinutes === -1) {
            this.expiryDate = this.previousExpiryDate !== null ? new Date(this.previousExpiryDate) : null
        } else if (this.expiryMinutes !== null) {
            this.expiryDate = new Date()
            this.expiryDate.setTime(this.expiryDate.getTime() + this.expiryMinutes * 60 * 1000)
        } else {
            this.expiryDate = null
        }
    }

    private updateValidation() {
        const linkDeliveryMethodControl = this.form.get('linkDeliveryMethod')
        if (this.isPaidInPerson) {
            linkDeliveryMethodControl?.clearValidators()
        } else {
            linkDeliveryMethodControl?.setValidators([Validators.required])
        }
        linkDeliveryMethodControl?.updateValueAndValidity()
    }

    private addCharge() {
        if (this.chargeType === 'cancellationCharge') {
            this.addCancellationCharge()
        } else {
            this.addDepositCharge()
        }
    }

    private addCancellationCharge() {
        const charge = this.charge
        if (charge === null) {
            return
        }
        const organisation = this.contextService.getOrganisation()
        if (organisation === null) {
            return
        }
        const linkDeliveryMethod = this.form.get('linkDeliveryMethod')?.value
        const sendViaEmail = linkDeliveryMethod === 'email'
        const sendViaSms = linkDeliveryMethod === 'sms'
        this.isAddingCharge = true
        this.bookingService.addCancellationCharge(
            organisation,
            this.business,
            this.venue,
            this.booking,
            charge,
            sendViaEmail,
            sendViaSms,
            this.expiryDate
        )
            .pipe(
                finalize(() => this.isAddingCharge = false),
                takeUntil(this.onDestroy$)
            )
            .subscribe({
                next: (booking) => {
                    this.bookingChanged.emit(booking)
                },
            })
    }

    private addDepositCharge() {
        const charge = this.charge
        if (charge === null) {
            return
        }
        const organisation = this.contextService.getOrganisation()
        if (organisation === null) {
            return
        }
        const isPaidInPerson = this.form.get('paidInPerson')?.value ?? false
        const linkDeliveryMethod = isPaidInPerson ? null : this.form.get('linkDeliveryMethod')?.value
        const sendViaEmail = !isPaidInPerson && linkDeliveryMethod === 'email'
        const sendViaSms = !isPaidInPerson && linkDeliveryMethod === 'sms'
        const isPaidImmediately = this.form.get('isPaidImmediately')?.value ?? false
        const expiryDate = this.forceNoExpiry ? null : this.expiryDate
        this.isAddingCharge = true
        this.bookingService.addDepositCharge(
            organisation,
            this.business,
            this.venue,
            this.booking,
            charge,
            sendViaEmail,
            sendViaSms,
            expiryDate,
            isPaidImmediately
        )
            .pipe(
                finalize(() => this.isAddingCharge = false),
                takeUntil(this.onDestroy$)
            )
            .subscribe({
                next: (booking) => {
                    this.bookingChanged.emit(booking)
                },
            })
    }

    private updateCharge() {
        const organisation = this.contextService.getOrganisation()
        if (organisation === null) {
            return
        }
        const charge = this.charge
        if (charge === null) {
            return
        }
        let expiryDate = undefined
        if (this.expiryMinutes !== null && this.expiryMinutes !== -1) {
            const now = new Date()
            expiryDate = new Date(now.getTime() + this.expiryMinutes * 60 * 1000)
        }
        this.isAddingCharge = true
        this.bookingService.updateCharge(
            organisation,
            this.business,
            this.venue,
            this.booking,
            charge,
            expiryDate,
        )
            .pipe(
                finalize(() => this.isAddingCharge = false),
                takeUntil(this.onDestroy$)
            )
            .subscribe({
                next: (booking) => {
                    this.bookingChanged.emit(booking)
                },
            })
    }

    protected readonly DurationUnit = DurationUnit

    get isPaidInPerson(): boolean {
        return this.form.get('paidInPerson')?.value ?? false
    }

    get isPaidImmediately(): boolean {
        return this.form.get('isPaidImmediately')?.value ?? false
    }

    get showExpiryDate(): boolean {
        return !this.isPaidInPerson && !this.forceNoExpiry && this.expiryDate !== null
    }

    get showExpiryTime(): boolean {
        return !this.isPaidInPerson && !this.forceNoExpiry
    }

    get showDeliveryMethods(): boolean {
        return !this.isPaidInPerson
    }

    get buttonText(): string {
        if (this.isPaidInPerson && this.chargeType === 'depositCharge') {
            return 'Add Deposit'
        }
        if (this.form.get('linkDeliveryMethod')?.value !== null) {
            return 'Send Link'
        }
        return 'Update'
    }
}
